'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import { terraProxyApiClient } from '../../clients/testerClient';
import {
  generateUrlPatternsFromTreeSelection,
  hydrateDomainTreeFromDto,
  type DomainTree,
} from '@terra/ui-design-system';

export const APPLICATION_DOMAIN_TREE_QUERY_KEY = 'application-domain-tree';

type UseApplicationDomainTreeOptions = {
  includeMethodNodes?: boolean;
  applyApplicationLevelRoot?: boolean;
  includeEndpoints?: boolean;
  includeWebPages?: boolean;
  type?: 'endpoints' | 'agents' | 'webpages' | 'ALL';
  enabled?: boolean;
  lifecycleState?: LifecycleStateFilterDto;
};

export const useInvalidateApplicationDomainTree = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async ({ applicationId }: { applicationId: string }) => {
      await queryClient.invalidateQueries({
        queryKey: [APPLICATION_DOMAIN_TREE_QUERY_KEY, applicationId],
        exact: false,
      });
    },
    [queryClient],
  );
};

export function useApplicationDomainTree({
  applicationId,
  options,
}: {
  applicationId: string | null | undefined;
  options?: UseApplicationDomainTreeOptions;
}) {
  const queryClient = useQueryClient();
  const includeMethodNodes = options?.includeMethodNodes ?? true;
  const applyApplicationLevelRoot = options?.applyApplicationLevelRoot ?? false;
  const includeEndpoints = options?.includeEndpoints ?? true;
  const includeWebPages = options?.includeWebPages ?? true;
  const type = options?.type;
  const enabled = options?.enabled ?? true;
  const lifecycleState = options?.lifecycleState;

  const result = useQuery({
    queryKey: [
      APPLICATION_DOMAIN_TREE_QUERY_KEY,
      applicationId,
      includeMethodNodes,
      applyApplicationLevelRoot,
      includeEndpoints,
      includeWebPages,
      type,
      lifecycleState,
    ],
    queryFn: async () => {
      if (!applicationId) {
        return null;
      }

      const response = await terraProxyApiClient.getDomainTree({
        applicationId,
        includeMethodNodes,
        applyApplicationLevelRoot,
        includeEndpoints,
        includeWebPages,
        type,
        ...(lifecycleState ? { lifecycleState } : {}),
      });

      return hydrateDomainTreeFromDto(response.roots);
    },
    enabled: Boolean(applicationId) && enabled,
    staleTime: 30000,
    structuralSharing: false,
  });

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [APPLICATION_DOMAIN_TREE_QUERY_KEY, applicationId],
      exact: false,
    });
  }, [applicationId, queryClient]);

  const domainTree: DomainTree | null = result.data ?? null;

  const generateUrlPatterns = useCallback(
    (selectedNodeIds: string[]) => {
      if (selectedNodeIds.length === 0) {
        return undefined;
      }

      if (!domainTree) {
        return [];
      }

      const patterns = generateUrlPatternsFromTreeSelection(
        selectedNodeIds,
        domainTree,
      );

      return patterns.length > 0 ? patterns : undefined;
    },
    [domainTree],
  );

  return {
    domainTree,
    generateUrlPatterns,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
    invalidate,
  };
}
